/**
 * Test ['and', 'or', 'not'] conditions
 * @module
 */

var _ = require('optimizely-client-fn');
var JSONEnv = require('./json');
var Logger = require('../utils/logger');

/**
 * @enum {string}
 */
var Operator = {
  AND: 'and',
  OR: 'or',
  NOT: 'not'
};

/**
 * @typedef {(Operator|Condition)} ConditionOrOperator
 */

/**
 * @typedef {module:lib/condition~ConditionOrOperator[]} Conditions
 */

/**
 * All conditions in an "and" group must be satisfied.
 *
 * @private
 * @param {Condition[]} conditions
 * @param {evaluatorFn} evaluator Function called to evaluate condition objects
 * @returns {boolean|undefined} True iff evaluator returns true for all conditions,
 * else false if none are undefined, else undefined
 */
function and(conditions:Condition[], evaluator:evaluatorFn): boolean|undefined {
  var eachResult;
  var hasUndefined;
  // All conditions in an "and" group must be satisfied.
  for (var i = 0; i < conditions.length; i++) {
    eachResult = evaluate(conditions[i], evaluator);
    if (eachResult === false) {
      return false;
    }
    if (_.isUndefined(eachResult)) {
      hasUndefined = true;
    }
  }
  // If we get to the end and nothing was false or undefined return true
  if (!hasUndefined) {
    return true;
  }
}

/**
 * Any condition in an "or" group may be satisfied.
 *
 * @private
 * @param {Condition[]} conditions
 * @param {evaluatorFn} evaluator Function called to evaluate condition objects
 * @return {boolean|undefined} true if evaluator returns true for any condition, else false if all results are false,
 * else undefined if some results are undefined
 */
function or(conditions:Condition[], evaluator:evaluatorFn): boolean|undefined {
  var eachResult;
  var hasUndefined = false;
  for (var i = 0; i < conditions.length; i++) {
    eachResult = evaluate(conditions[i], evaluator);
    if (eachResult === true) {
      return true;
    }
    if (_.isUndefined(eachResult)) {
      hasUndefined = true;
    }
  }
  // If we get to the end and nothing was true or undefined return false
  if (!hasUndefined) {
    return false;
  }
}

/**
 * No condition in a "not" group may be satisfied.
 *
 * @private
 * @param {Condition[]} singleCondition Condition(s) to evaluate (must have length 1).
 * @param {evaluatorFn} evaluator Function called to evaluate condition objects.
 * @return {boolean|undefined} true if the condition evaluates false, false if the condition evaluates true, undefined
 * if the condition evaluates undefined
 */
function not(singleCondition:Condition[], evaluator:evaluatorFn): boolean|undefined {
  // No conditions in a "not" group may be satisfied.
  if (singleCondition.length !== 1) {
    return false;
  }
  var result = evaluate(singleCondition[0], evaluator);
  // If result is not undefined, return negated result
  if (!_.isUndefined(result)) {
    return !result;
  }
}

var operators = {} as any;
operators[Operator.AND] = and;
operators[Operator.OR] = or;
operators[Operator.NOT] = not;

/**
 * Determine if conditions are satisfied.
 *
 * Ported from condition.compoundTest in js/client/condition.js
 *
 * @param {module:lib/condition~Conditions} conditions Conditions to apply. Evaluates recursively.
 *        ```Grammar: conditions := ["and"|"or", [(conditions|Object), ...]] | ["not", (conditions|Object)] | Object```
 * @param {evaluatorFn} evaluator
 * @return {boolean|undefined} true/false if a result can be determined; undefined if no result can be determined
 * (because evaluator does not return a defined value for some required condition)
 * @throws {Error} Any error thrown by the evaluator at any point during the recursive evaluation.
 */
function evaluate(conditions:Condition|Condition[], evaluator:evaluatorFn): boolean|undefined {
  var result;

  if (_.isArray(conditions)) {
    // This is a grouping of condition objects or condition arrays.
    var operator;
    var args;
    if (conditions[0] in operators) {
      // Operator to apply is explicit
      operator = conditions[0];
      args = (conditions as Condition[]).slice(1);
    } else {
      // Operator to apply is not explicit - assume 'or'
      operator = Operator.OR;
      args = conditions;
    }

    Logger.groupCollapsed('Condition / Applying operator "' + operator + '" with args', JSONEnv.stringify(args));
    try {
      result = operators[operator](args, evaluator);
      Logger.debug('Condition / Result:', result);
    } finally {
      // Ensure the group gets closed to avoid swallowing the rest of the logs
      Logger.groupEnd();
    }
    return result;
  }

  // apply the evaluator to the single condition
  result = evaluator(conditions as Condition);
  Logger.debug('Condition / Evaluated:', JSONEnv.stringify(conditions), ':', result);
  return result;
}

module.exports = {
  evaluate: evaluate,
};
